(ns manage.ad-variant-preview.viewport
  (:require ["d3" :as d3]
            ["d3-zoom" :as d3-zoom]
            [uix.core :as uix :refer [$ defhook defui]]))

(def Ctx
  (uix/create-context {:d3-zoom-ref nil
                       :pan-area-el-ref nil
                       :focused-item-ref nil
                       :event-target nil
                       :w-a nil
                       :h-a nil
                       :mode nil
                       ;; Scaling value for non-canvas views
                       :item-scale nil
                       ;; Non-reactive reference for event handlers
                       :video-status-ref nil
                       ;; Selected Meta carousel preview format ("1:1" etc.), shared by every
                       ;; previewed variant. nil outside the preview page, where each carousel
                       ;; falls back to its own local choice instead.
                       :preview-format nil
                       :set-preview-format nil}))

(def CtxProvider (.-Provider Ctx))

(defhook use-context []
  (uix/use-context Ctx))

(defn dispatch
  ([event-target event-type]
   (.dispatchEvent event-target (js/Event. event-type)))
  ([event-target event-type m]
   (let [ex (js/Event. event-type)]
     (set! (.-data ex) m)
     (.dispatchEvent event-target ex))))

(def MAX-ZOOM 1)
(def ZOOM-LEVELS [0.25 0.5 0.75 1.0])

;; (def ZOOM-FACTOR 0.75)

(defhook use-measure-preview-item
  "Preview item sizes are static, so we don't need ResizeObserver.

  We are interested in pre-transform width/height (offset size)
  and position of the item inside preview item grid (offset position)."
  [set-item-places uuid]
  ;; TODO: What about browser zoom level changes?
  (uix/use-callback (fn [el]
                      (when set-item-places
                        (if el
                          (let [m {:w (.-offsetWidth el)
                                   :h (.-offsetHeight el)
                                   :x (.-offsetLeft el)
                                   :y (.-offsetTop el)}]
                            (set-item-places #(assoc % uuid m)))
                          (set-item-places #(dissoc % uuid)))))
                    [set-item-places uuid]))

(defn find-closest-item
  "Find item that is closest to the center of the viewport"
  [viewport item-places w-a h-a]
  (let [[x y scale] viewport
        ;; Take the center coordinate of viewport and translate that into item coordinates
        x-c (/ w-a 2)
        y-c (/ h-a 2)
        t (-> d3-zoom/zoomIdentity
              (.translate x y)
              (.scale scale))
        [x-a y-a] (.invert t #js [x-c y-c])]
    ;; (js/console.log "p_a" x-a y-a)
    (-> (reduce (fn [{:keys [d] :as acc} [uuid {:keys [x y w h] :as measurement}]]
                  (let [x-inside (<= x x-a (+ x w))
                        y-inside (<= y y-a (+ y h))
                        ;; If point is inside the item, distance for the axis is 0.
                        ;; Else choose the distance to corner of the item that is closer to the point.
                        d-new (+ (if x-inside
                                   0
                                   (min (abs (- x-a x))
                                        (abs (- x-a (+ x w)))))
                                 (if y-inside
                                   0
                                   (min (abs (- y-a y))
                                        (abs (- y-a (+ y h))))))]
                    ;; (js/console.log uuid x y (+ x w) (+ y h) x-inside y-inside d-new)
                    (if (or (nil? d) (< d-new d))
                      {:uuid uuid
                       :measurement measurement
                       :d d-new}
                      acc)))
                {:uuid nil
                 :measurement nil
                 :d nil}
                item-places)
        (dissoc :d))))

(defn center-on-coords [ctx {:keys [x y w h]}]
  (let [{:keys [^js d3-zoom-ref pan-area-el-ref w-a h-a set-viewport]} ctx
        inst (.-current d3-zoom-ref)
        el (.-current pan-area-el-ref)
        selection (.select d3 el)]
    (if inst
      (do
        (.scaleTo inst selection 1.0)
        (.translateTo inst selection
                      (+ x (int (/ w 2)))
                      (+ y (int (/ h 2)))
                      #js [(int (/ w-a 2)) (int (/ h-a 2))]))
      (do
        ;; Also move the list view scroll
        (set! (.-scrollLeft (.-current pan-area-el-ref)) x)
        (set! (.-scrollTop (.-current pan-area-el-ref)) y)
        ;; Update the minimap - might be updated anyway because the view
        ;; updates it after scroll.
        (set-viewport (fn [[x y _k]] [x y 1.0]))))))

(defn center-on-focused [ctx]
  (let [{:keys [focused-item-ref]} ctx
        focused-item (.-current focused-item-ref)
        coords (:measurement focused-item)]
    (center-on-coords ctx coords)))

(defn fit-to-view [ctx]
  ;; (js/console.log "fit to view" (pr-str focused-item))
  (let [{:keys [^js d3-zoom-ref pan-area-el-ref focused-item-ref w-a h-a set-viewport item-scale]} ctx
        focused-item (.-current focused-item-ref)
        ;; FIXME: On non canvas this is the rendered element size, which is
        ;; based on the previous scale
        {:keys [x y w h]} (:measurement focused-item)
        ;; Workaround the reverse the non-canvas scaling?
        ;; FIXME: Element height is funny with scaling so reversing the scaling like this doesn't
        ;; really work.
        w (or (get-in focused-item [:dimensions :w])
              (if item-scale (* (/ 1 item-scale) w) w))
        h (or (get-in focused-item [:dimensions :h])
              (if item-scale (* (/ 1 item-scale) h) h))


        inst (.-current d3-zoom-ref)
        el (.-current pan-area-el-ref)
        selection (.select d3 el)
        ;; Find the largest zoom that allows this item to fit into the view
        ;; Use 90% of the viewport
        fit-scale (max (min (/ (* 0.90 w-a) w)
                            (/ (* 0.90 h-a) h)
                            MAX-ZOOM)
                       (:min-zoom ctx))]
    ;; (js/console.log w-a h-a w h (pr-str (:measurement focused-item)) fit-scale)
    (if inst
      (do
        (.scaleTo inst selection fit-scale)
        ;; Move the center coordinate of the focused item to center of the viewport
        ;; Use int coordinates so the position is aligned with pixels on k=1.0
        (.translateTo inst selection
                      (+ x (int (/ w 2)))
                      (+ y (int (/ h 2)))
                      #js [(int (/ w-a 2)) (int (/ h-a 2))]))
      (set-viewport (fn [[x y _k]] [x y fit-scale])))))

(defn new-zoom-level
  "Find the next zoom level either in or out"
  [k in]
  (reduce (fn [current next-zoom]
            (if (if in
                  ;; going to the next smaller level cmpared to k
                  (<= k next-zoom)
                  ;; going to the next larger level compared to k
                  (>= k next-zoom))
              (reduced current)
              next-zoom))
          k
          (if in
            ZOOM-LEVELS
            (reverse ZOOM-LEVELS))))

(defn zoom [ctx in]
  (let [{:keys [^js d3-zoom-ref pan-area-el-ref set-viewport]} ctx
        inst (.-current d3-zoom-ref)
        el (.-current pan-area-el-ref)
        selection (.select d3 el)
        ;; for .scaleBy version
        #_#_k-m (if in ZOOM-FACTOR (/ 1 ZOOM-FACTOR))]
    ;; optional p parameter is already defaulting to center of the viewport
    (if inst
      (let [current-t (.zoomTransform d3 (.node selection))
            k (.-k current-t)]
        (.scaleTo inst selection (new-zoom-level k in)))
      (set-viewport (fn [[x y k]] [x y (new-zoom-level k in)])))))

(defn zoom-to [ctx k]
  (let [{:keys [^js d3-zoom-ref pan-area-el-ref set-viewport]} ctx
        inst (.-current d3-zoom-ref)
        el (.-current pan-area-el-ref)
        selection (.select d3 el)]
    (if inst
      (.scaleTo inst selection k)
      (set-viewport (fn [[x y _k]] [x y k])))))

(defhook use-d3-zoom [{:keys [d3-zoom-ref pan-area-el-ref set-viewport] :as ctx} set-pan-active]
  ;; TODO: Apply extent/translateExtent/constrain to keep items in view
  ;; TODO: zoom.filter could change which events trigger zoom? what
  ;; about panning?
  (uix/use-effect (fn []
                    (let [inst (d3-zoom/zoom)
                          zoom (.. inst
                                   (scaleExtent #js [(:min-zoom ctx) MAX-ZOOM])
                                   (on "zoom" (fn [e]
                                                (let [t (.-transform e)]
                                                  (set-viewport [(.-x t) (.-y t) (.-k t)]))))
                                   (on "start" (fn [_e] (set-pan-active true)))
                                   (on "end" (fn [_e] (set-pan-active false))))
                          el (.-current pan-area-el-ref)
                          selection (.select d3 el)]
                      ;; TODO: Apply the old viewport on initialization
                      ;; (.transform zoom selection (-> d3-zoom/zoomIdentity
                      ;;                                (.translate x y)
                      ;;                                (.scale scale)))
                      (reset! d3-zoom-ref inst)
                      (.call selection zoom)
                      (fn []
                        (reset! d3-zoom-ref nil)
                        (.on selection "zoom" nil))))
                  ;; Both arguments are stable ref values
                  ^:lint/disable
                  []))

(defn keep-focused-item-position
  "Follow cases where item position changes for focused item,
  for example previous item in the grid has been unselected.
  Then pan the view to keep the item in focus.
  Doesn't work for cases where new items are added before? Because
  the focused-item is already set to a new item in those cases."
  [^js d3-zoom-ref pan-area-el-ref old new]
  (let [inst (.-current d3-zoom-ref)
        el (.-current pan-area-el-ref)
        selection (.select d3 el)
        dx (- (:x old) (:x new))
        dy (- (:y old) (:y new))]
    (when (and inst
               (or (not (zero? dx))
                   (not (zero? dy))))
      ;; (js/console.log "focused item moved" dx dy)
      (.translateBy inst selection dx dy))))

(defui pan-area [{:keys [pan-area-el-ref viewport children pan-active]}]
  (let [[x y k] viewport]
    ($ :div
       {:ref pan-area-el-ref
        :style {:width "100%"
                :height "100%"
                :overflow "hidden"}}
       ($ :div
          ;; Use int numbers even when scaling has made the numbers not align with pixels.
          {:style {:transform (str "translate(" (int x) "px, " (int y) "px) scale(" k ")")
                   :transform-origin "0 0 0"
                   ;; Make (initial & regular) fit-to-view and keep focused item position smooth
                   :transition (when-not pan-active
                                 "transform 300ms ease")}}
          children))))

(defui debug-cross []
  ;; For debugging
  ($ :<>
     ($ :div
        {:style {:background "red"
                 :opacity 0.5
                 :pointer-events "none"
                 :width "100%"
                 :height 2
                 :position "absolute"
                 :left 0
                 :top "50%"
                 :margin-top "-1px"}})
     ($ :div
        {:style {:background "red"
                 :opacity 0.5
                 :pointer-events "none"
                 :width 2
                 :height "100%"
                 :position "absolute"
                 :top 0
                 :left "50%"
                 :margin-left "-1px"}})))
